import { SCALE_TYPES, UNKNOWN_COLOR } from '@carto/kepler.gl/constants'
import { colorLabeler } from '../utils/legendLabels'
import { LayerFeatureType, getLayerFeatureType } from '../utils/layerType'
import { Layer } from '@carto/kepler.gl/layers'
import { LEGEND_TYPES } from '@carto/meridian-ds/widgets'
import {
  DEFAULT_MAP_INDEX,
  FormattedLabel,
  KeplerLayer,
  LayerLegendEntrySettings,
  LayerLegendSettings,
  LegendEntry,
  LegendSettings,
  LegendSettingsVersion,
  LegendWidgetProps,
  RasterLayerConfigColorBand,
  RasterStyleType
} from 'features/builder/types'
import { createHash } from 'crypto'
import { Color } from '@deck.gl/core'
import { extraColors } from 'features/common/ui/theme/cartoTheme'
import { getRasterBandLabels, RASTER_COLOR_BANDS } from '../ui/layers/raster/rasterLayerConfig'

export enum LegendVisualChannels {
  color = 'color',
  strokeColor = 'strokeColor',
  uniqueValuesColor = 'uniqueValuesColor',
  size = 'size',
  weight = 'weight',
  customMarkers = 'customMarkers',
  radius = 'radius'
}

export function migrateLegendSettings(legendSettings: LegendSettings) {
  if (legendSettings.baseMapSelector !== undefined) {
    legendSettings = {
      ...legendSettings,
      baseMapSelector: undefined
    }
  }
  if (legendSettings.expanded === undefined) {
    legendSettings = {
      ...legendSettings,
      expanded: { [DEFAULT_MAP_INDEX]: false }
    }
  }
  const version = legendSettings.version ?? LegendSettingsVersion.V0
  if (version === LegendSettingsVersion.V0) {
    const oldLegendSettings = legendSettings
    legendSettings = {
      ...legendSettings,

      // old `active` means `shouldDisplayEntries` now
      // new `active` is always on for old maps
      layers: Object.keys(oldLegendSettings.layers).reduce((acc, layerId) => {
        acc[layerId] = {
          ...oldLegendSettings.layers[layerId],
          active: true,
          shouldDisplayEntries: oldLegendSettings.layers[layerId].active
        }
        return acc
      }, {}),

      version: LegendSettingsVersion.V1
    }
  }

  return legendSettings
}

const fieldName = (field) => field?.displayName || field?.name
const strokeColorLegend = (colorProps) => colorLegend(colorProps, { displayAsStroke: true })
const aggregatedColorLegend = (colorProps) => colorLegend(colorProps, { aggregated: true })

const colorLegend = (colorProps, { displayAsStroke = false, aggregated = false } = {}): LegendWidgetProps => {
  const { layerName, property, field, domain, range, scale, layerConfig, legendSettings } = colorProps

  if (legendSettings) {
    const { labels, colors } = legendSettings.reduce(
      (prev, current) => {
        prev.labels.push(current.label)
        prev.colors.push(current.color)

        return prev
      },
      { labels: [], colors: [] }
    )

    return {
      type: LEGEND_TYPES.CATEGORY,
      basedOn: fieldName(field),
      colors,
      labels,
      isStrokeColor: displayAsStroke,
      customMarkers:
        layerConfig.visConfig.customMarkers &&
        !(layerConfig.customMarkersField && layerConfig.visConfig.customMarkersRange)
          ? layerConfig.visConfig.customMarkersUrl
          : undefined
    }
  }

  if (!field && !aggregated) {
    return {
      type: LEGEND_TYPES.CATEGORY,
      colors: [property],
      labels: [layerName],
      isStrokeColor: displayAsStroke,
      customMarkers:
        layerConfig.visConfig.customMarkers &&
        !(layerConfig.customMarkersField && layerConfig.visConfig.customMarkersRange)
          ? layerConfig.visConfig.customMarkersUrl
          : undefined
    }
  }

  const {
    labels,
    data: colors,
    customMarkers
  } = colorLabeler(domain, range, scale, field?.type, !displayAsStroke, layerConfig)

  return {
    type: LEGEND_TYPES.CATEGORY,
    basedOn: fieldName(field),
    isStrokeColor: displayAsStroke,
    colors,
    labels,
    truncated: scale !== SCALE_TYPES.ordinal ? 0 : Math.max(domain.length - colors.length, 0),
    customMarkers
  }
}

const customMarkersLegend = (customMarkerProps): LegendWidgetProps | null => {
  const { field, domain, range, layerName, layerConfig } = customMarkerProps

  if (!layerConfig.visConfig.customMarkers) {
    return null
  }

  const maskedMarkers = Boolean(layerConfig.visConfig.filled)
  if (!field && !layerConfig.visConfig.filled) {
    return {
      type: LEGEND_TYPES.CATEGORY,
      colors: [''],
      labels: [layerName],
      isCustomMarkers: true,
      maskedMarkers,
      customMarkers:
        layerConfig.visConfig.customMarkers &&
        !(layerConfig.customMarkersField && layerConfig.visConfig.customMarkersRange)
          ? layerConfig.visConfig.customMarkersUrl
          : undefined
    }
  }
  if (!field) {
    return null
  }

  const labels: string[] = []
  const colors: string[] | Color[] = []
  const customMarkers: string[] = []
  const fallbackMarker = range?.othersMarker || layerConfig.visConfig.customMarkersUrl
  if (range?.markerMap) {
    for (const { value, markerUrl } of range.markerMap) {
      if (markerUrl) {
        labels.push(value)
        // @ts-expect-error Conflict in Kepler and Builder types.
        colors.push(UNKNOWN_COLOR)
        customMarkers.push(markerUrl)
      }
    }
  }
  const truncated = Math.max(domain.length - (range?.markerMap?.length || 0), 0)

  return {
    type: LEGEND_TYPES.CATEGORY,
    isCustomMarkers: true,
    fallbackMarker,
    basedOn: fieldName(field),
    colors,
    labels,
    customMarkers,
    maskedMarkers,
    truncated
  }
}

const radiusLegend = ({ domain, field }): LegendWidgetProps => {
  const [min, max] = [domain[0], domain.slice(-1)[0]]

  return {
    basedOn: fieldName(field),
    type: LEGEND_TYPES.PROPORTION,
    labels: [min, max]
  }
}

const heatmapLegend = (layer: Layer): LegendWidgetProps => {
  return {
    basedOn: 'density',
    type: LEGEND_TYPES.CONTINUOUS_RAMP,
    colors: layer.config.visConfig.colorRange.colors,
    labels: [
      { value: 0, label: 'Low' },
      { value: 100, label: 'High' }
    ]
  }
}

const rgbRasterLegend = (layer: KeplerLayer): LegendWidgetProps => {
  const colorBands = layer.config.visConfig.colorBands || []

  const enabledBands = RASTER_COLOR_BANDS
    // find configured bands
    .map((band) => colorBands.find((colorBand) => colorBand.band === band))
    // filter out disabled bands
    .filter((band) => band && band.type !== 'none') as RasterLayerConfigColorBand[]

  return {
    basedOnLabelId: 'builder.legendManager.entryType.rasterRgb',
    type: LEGEND_TYPES.CATEGORY,
    colors: enabledBands.map((band) => extraColors.colorBands[band.band]),
    labels: enabledBands.map((band) => band.value || '')
  }
}

/**
 * Raster datasets may include an optional per-band 'valuelabels' mapping, from
 * band values to human-readable labels.
 */
const colorRasterLegend = (layer: KeplerLayer) => {
  const rasterMetadata = layer.meta?.rasterMetadata

  return (...args: Parameters<typeof colorLegend>): LegendWidgetProps => {
    const field = args[0].field
    const widgetProps = colorLegend(...args)

    const valueLabels = getRasterBandLabels(rasterMetadata, field.name)
    if (valueLabels && widgetProps.labels) {
      widgetProps.labels = widgetProps.labels.map((label: string | FormattedLabel) => {
        const value = typeof label === 'string' ? parseInt(label) : label.value
        return valueLabels[value] ?? String(value)
      })
    }
    return widgetProps
  }
}

const visualChannelToLegend = (layer: KeplerLayer, visualChannelKey: string, legendFactory): LegendEntry | null => {
  const visualChannel = layer.visualChannels?.[visualChannelKey]

  if (!visualChannel) {
    return null
  }

  const { condition, property, domain, field, scale, range } = visualChannel

  // check if the channel is actually used to draw features
  if (condition && !condition(layer.config)) {
    return null
  }

  // additional conditions, to avoid useless legend entries:
  // omit radius/size when it's not based on a field (i.e. it's a fixed value)
  if (visualChannelKey === LegendVisualChannels.size && !layer.config.sizeField) {
    return null
  }
  // omit stroke when it's not based on a field and a fill color is already used
  if (
    visualChannelKey === LegendVisualChannels.strokeColor &&
    getLayerFeatureType(layer) !== LayerFeatureType.LINE &&
    !layer.config.strokeColorField &&
    layer.config.visConfig.filled
  ) {
    return null
  }

  // Some layers are drawn with a stroke even if it hasn't been explicitely defined,
  // using the same color as the `color` visual channel by default
  let fallbackProperty
  if (visualChannelKey === LegendVisualChannels.strokeColor) {
    fallbackProperty = 'color'
  }

  const props = {
    layerName: layer.config.label,
    property:
      layer.config[property] ||
      layer.config.visConfig[property] ||
      layer.config[fallbackProperty] ||
      layer.config.visConfig[fallbackProperty],
    domain: layer.config[domain],
    field: layer.config[field],
    scale: layer.config[scale],
    range: layer.config.visConfig[range],
    layerConfig: layer.config,
    legendSettings: layer.legendSettings[property]
  }

  const widgetProps = legendFactory(props)
  if (!widgetProps) {
    return null
  }
  return {
    widgetProps,
    visualChannel: visualChannelKey
  }
}

export const legendEntriesWithCustomSettings = (layer: KeplerLayer, layerSettings: LayerLegendSettings | undefined) => {
  const entries = layerToLegendEntries(layer)
  return entries.map((entry) =>
    applyCustomEntrySettings(
      entry,
      layerSettings?.entries?.find((entrySettings) => entrySettings.visualChannel === entry.visualChannel)
    )
  )
}

const applyCustomEntrySettings = (entry: LegendEntry, entrySettings: LayerLegendEntrySettings | undefined) => {
  if (!entrySettings || entry.hash !== entrySettings.hash) {
    return entry
  }
  const customizedEntry = { ...entry }
  if (entrySettings.customFieldLabel) {
    customizedEntry.widgetProps.basedOn = entrySettings.customFieldLabel
  }
  if (entrySettings.customColorLabels) {
    customizedEntry.widgetProps.labels = (customizedEntry.widgetProps.labels || []).map(
      (label, i) => entrySettings.customColorLabels?.[i]?.trim() || label
    )
  }
  return customizedEntry
}

export const layerToLegendEntries = (layer: KeplerLayer): LegendEntry[] => {
  const featureType = getLayerFeatureType(layer)

  if (featureType === LayerFeatureType.HEATMAP) {
    // Special case, as heatmap 'weight' visualChannel doesn't point to real fields
    return [{ visualChannel: LegendVisualChannels.weight, widgetProps: heatmapLegend(layer) }]
  }

  let styleEntries: [LegendVisualChannels, (any) => LegendWidgetProps | null][] = []

  switch (featureType) {
    case LayerFeatureType.LINE:
      styleEntries = [[LegendVisualChannels.strokeColor, colorLegend]]
      break
    case LayerFeatureType.POLYGON:
    case LayerFeatureType.H3:
      styleEntries = [
        [LegendVisualChannels.color, colorLegend],
        [LegendVisualChannels.strokeColor, strokeColorLegend]
      ]
      break
    case LayerFeatureType.GRID:
    case LayerFeatureType.HEXAGON:
      styleEntries = [[LegendVisualChannels.color, aggregatedColorLegend]]
      break
    case LayerFeatureType.CLUSTER:
      if (layer.config.visConfig.colorAggregation) {
        styleEntries = [[LegendVisualChannels.color, aggregatedColorLegend]]
      } else {
        styleEntries = [[LegendVisualChannels.color, colorLegend]]
      }
      break
    case LayerFeatureType.RASTER:
      if (layer.config.visConfig.rasterStyleType === RasterStyleType.Rgb) {
        // shortcut for RGB, that creates categories legend entry without any channel
        return [{ visualChannel: 'rgb', widgetProps: rgbRasterLegend(layer) }]
      } else if (layer.config.visConfig.rasterStyleType === RasterStyleType.ColorRange) {
        styleEntries = [[LegendVisualChannels.color, colorLegend]]
      } else {
        styleEntries = [[LegendVisualChannels.uniqueValuesColor, colorRasterLegend(layer)]]
      }
      break
    case LayerFeatureType.POINT:
      styleEntries = [
        [LegendVisualChannels.color, colorLegend],
        [LegendVisualChannels.strokeColor, strokeColorLegend],
        [LegendVisualChannels.customMarkers, customMarkersLegend],
        [LegendVisualChannels.radius, radiusLegend],
        [LegendVisualChannels.size, radiusLegend]
      ]
      break
    default:
      styleEntries = [
        [LegendVisualChannels.color, colorLegend],
        [LegendVisualChannels.strokeColor, strokeColorLegend],
        [LegendVisualChannels.customMarkers, customMarkersLegend],
        [LegendVisualChannels.size, radiusLegend]
      ]
  }

  return styleEntries
    .map(([visualChannel, legendEntryCreator]) => visualChannelToLegend(layer, visualChannel, legendEntryCreator))
    .filter(
      (legendEntry: LegendEntry | null): legendEntry is LegendEntry =>
        legendEntry !== null &&
        !(legendEntry.widgetProps.type === LEGEND_TYPES.PROPORTION && !legendEntry.widgetProps.basedOn)
    )
    .map(tagEntry)
}

const tagEntry = (entry: LegendEntry) => {
  // Properties that should invalidate custom settings when changed
  // For example, changing the color palette doesn't need to clear previous custom settings
  const invalidatingProps = {
    type: entry.widgetProps.type,
    basedOn: entry.widgetProps.basedOn,
    labels: entry.widgetProps.labels?.length
  }
  const hash = createHash('md5').update(JSON.stringify(invalidatingProps)).digest('hex')
  return { ...entry, hash }
}
